/**
 * Blog API Service
 * Isolated feature: All blog-related API calls following FRONTEND_API_GUIDE
 */
import { apiClient } from '@/shared/services/apiClient';
import { API_ENDPOINTS } from '@/shared/constants';
import { logger } from '@/shared/utils';
import { ENV } from '@/config/environment';
import type { ApiResponse } from '@/shared/types';

// Standardized response format from API guide
export interface StandardResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  timestamp?: string;
}

// apiClient.get/post/put/delete already unwrap the single backend envelope
// ({success, data, error, timestamp}) into ApiResponse<T> - do not treat the
// generic as a second nested StandardResponse<T>, or `.data` reads past the
// real payload and `.success`/`.data` checks on the result always fail.
function toStandardResponse<T>(response: ApiResponse<T>): StandardResponse<T> {
  return {
    success: response.success,
    message: response.message,
    data: response.data,
    error: response.error
      ? {
          code: (response.error as { code?: string; type?: string }).code || response.error.type || 'unknown_error',
          message: response.error.message || 'Unknown error',
          details: response.error.details,
        }
      : undefined,
  };
}

// Response types matching FRONTEND_API_GUIDE
export interface BlogAuthor {
  id: string;
  username: string;
  full_name: string;
  avatar_url?: string;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image_url?: string;
  author: BlogAuthor;
  category: string;
  tags: string[];
  is_published: boolean;
  is_featured: boolean;
  published_at?: string;
  created_at?: string;
  updated_at?: string;
  reading_time_minutes: number;
  view_count: number;
  like_count: number;
  comment_count: number;
  seo?: {
    meta_description: string;
    meta_keywords: string;
  };
}

/** Raw blog payload from backend (create/fetch/list). */
export interface BackendBlogRaw {
  id?: string | number;
  blog_id?: string;
  title?: string;
  slug?: string;
  excerpt?: string | null;
  content?: string | null;
  featured_image_url?: string | null;
  is_published?: boolean;
  is_featured?: boolean;
  author_id?: string | number | null;
  author_username?: string | null;
  author_avatar_url?: string | null;
  author?: BlogAuthor;
  views?: number;
  likes_count?: number;
  comments_count?: number;
  reading_time_minutes?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  published_at?: string | null;
  tags?: string[] | string | null;
  category?: string | null;
  seo?: Record<string, unknown> | null;
}

export function getBlogIdFromRaw(data?: BackendBlogRaw | null): string | undefined {
  if (!data) return undefined;
  const id = data.blog_id ?? data.id;
  return id != null && String(id).length > 0 ? String(id) : undefined;
}

export function mapBackendBlogToPost(blog: BackendBlogRaw): BlogPost {
  const blogId = getBlogIdFromRaw(blog) ?? '';

  let tags: string[] = [];
  if (blog.tags) {
    if (Array.isArray(blog.tags)) {
      tags = blog.tags;
    } else {
      tags = String(blog.tags).split(',').map((t) => t.trim()).filter(Boolean);
    }
  }

  let seo: BlogPost['seo'];
  if (blog.seo && typeof blog.seo === 'object') {
    const seoObj = blog.seo as Record<string, unknown>;
    seo = {
      meta_description: (seoObj.meta_description as string) || '',
      meta_keywords: (seoObj.meta_keywords as string) || '',
    };
  }

  const author: BlogAuthor = blog.author
    ? blog.author
    : {
        id: String(blog.author_id ?? ''),
        username: blog.author_username || 'Unknown',
        full_name: blog.author_username || 'Unknown',
        avatar_url: blog.author_avatar_url || undefined,
      };

  return {
    id: blogId,
    title: blog.title || '',
    slug: blog.slug || '',
    excerpt: blog.excerpt || '',
    content: blog.content || blog.excerpt || '',
    featured_image_url: blog.featured_image_url || undefined,
    author,
    category: blog.category || '',
    tags,
    is_published: blog.is_published ?? false,
    is_featured: blog.is_featured ?? false,
    published_at: blog.published_at || blog.created_at || undefined,
    created_at: blog.created_at || undefined,
    updated_at: blog.updated_at || undefined,
    reading_time_minutes: blog.reading_time_minutes || 0,
    view_count: blog.views ?? 0,
    like_count: blog.likes_count ?? 0,
    comment_count: blog.comments_count ?? 0,
    seo,
  };
}

// Blog list response with pagination
export interface BlogListData {
  blogs: BlogPost[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

// Response data types (backend may return blog_id or id, plus full blog fields)
export interface CreateBlogData extends BackendBlogRaw {
  blog_id: string;
  status?: string;
}

export interface UpdateBlogData {
  blog_id: string;
  status: string;
  updated_at: string;
}

export interface DeleteBlogData {
  blog_id: string;
  status: string;
  deleted_at: string;
}

// API Response Types
export interface BlogsResponse extends StandardResponse<BlogListData> {
  data: BlogListData;  // Make data required for BlogsResponse
}

export interface BlogResponse extends StandardResponse<BlogPost> {
  data: BlogPost;  // Make data required for BlogResponse
}

export type CreateBlogResponse = StandardResponse<CreateBlogData>;
export type UpdateBlogResponse = StandardResponse<UpdateBlogData>;
export type DeleteBlogResponse = StandardResponse<DeleteBlogData>;

// Request Types
export interface CreateBlogRequest {
  title: string;
  content: string;
  excerpt?: string;
  category: string;
  tags: string[];
  featured_image_url?: string;
  is_published?: boolean;
  meta_description?: string;
  meta_keywords?: string;
}

export interface UpdateBlogRequest {
  blog_id: string;
  title?: string;
  content?: string;
  excerpt?: string;
  category?: string;
  tags?: string[];
  featured_image_url?: string;
  is_published?: boolean;
  meta_description?: string;
  meta_keywords?: string;
}
// Publish blog response (matches API guide)
export type PublishBlogResponse = StandardResponse<{
  blog_id: string;
  published_at: string;
}>;

export interface BlogCategory {
  name: string;
  count: number;
  slug: string;
  description?: string;
}

export interface TagSuggestion {
  tag: string;
  count: number;
  popularity_score?: number;
}

export interface BlogComment {
  id: number;
  content: string;
  author: {
    id: number;
    username: string;
    full_name: string;
    avatar_url?: string;
  };
  created_at: string;
  updated_at?: string;
  like_count: number;
  reply_count: number;
  is_approved: boolean;
  parent_comment_id?: number;
  replies?: BlogComment[];
}

export interface BlogFilters {
  page?: number;
  limit?: number;
  category?: string;
  search?: string;
  author?: string;
  sort?: 'date_desc' | 'date_asc' | 'popularity' | 'title';
  tags?: string[];
  is_published?: boolean; // Filter by published status (maps to 'published' query param)
}

export class BlogApiService {
  // Check if mock mode is enabled (DEV ONLY - NEVER in production)
  private isMockMode(): boolean {
    if (import.meta.env.PROD) {
      // NEVER use mock data in production
      return false;
    }
    return ENV.app.localDevMode || import.meta.env.VITE_USE_MOCK_DATA === 'true';
  }

  async getBlogs(filters: BlogFilters = {}): Promise<BlogsResponse> {
    // MOCK MODE: Return mock data if enabled (DEV ONLY)
    if (this.isMockMode()) {
      console.warn('⚠️ MOCK MODE ENABLED - Using mock blog data. This should NEVER be used in production!');
      const { localDevBlogList, mockBlogList } = await import('./mockData');
      
      // Apply basic filtering
      let filteredBlogs = ENV.app.localDevMode ? localDevBlogList : mockBlogList;
      if (filters.is_published !== undefined) {
        filteredBlogs = filteredBlogs.filter(blog =>
          blog.is_published === filters.is_published
          && (filters.is_published || blog.id !== 'local-dev-archived-blog')
        );
      } else if (ENV.app.localDevMode) {
        filteredBlogs = filteredBlogs.filter(blog => blog.is_published);
      }
      if (filters.category) {
        filteredBlogs = filteredBlogs.filter(blog => blog.category === filters.category);
      }
      
      return {
        success: true,
        message: 'Mock data loaded (DEV ONLY)',
        data: {
          blogs: filteredBlogs,
          total: filteredBlogs.length,
          page: 1,
          per_page: filteredBlogs.length,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        },
      };
    }

    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Map is_published to 'published' query param (backend expects 'published')
        if (key === 'is_published') {
          queryParams.append('published', String(value));
        } else if (Array.isArray(value)) {
          value.forEach(item => queryParams.append(key, String(item)));
        } else {
          queryParams.append(key, String(value));
        }
      }
    });
    
    const url = `${API_ENDPOINTS.BLOGS.FETCHALL}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    // Log the request (debug level)
    logger.debug('Fetching blogs', 'BlogApiService', { url, filters });
    
    try {
      const response = await apiClient.get<BlogListData>(url);
      
      // Log the response (debug level)
      logger.debug('Blogs fetched', 'BlogApiService', { 
        success: response?.success,
        count: response?.data?.blogs?.length || 0
      });
      
      // The apiClient.get already returns ApiResponse<T>, so response is { success, data, ... }
      // We need to return it as BlogsResponse which extends StandardResponse<BlogListData>
      return response as BlogsResponse;
    } catch (error) {
      logger.error('Error fetching blogs', 'BlogApiService', { error, url, filters });
      throw error;
    }
  }

  async getBlogById(blogId: string, options?: { preview?: boolean }): Promise<BlogResponse> {
    // MOCK MODE: Return mock data if enabled (DEV ONLY)
    if (this.isMockMode()) {
      console.warn('⚠️ MOCK MODE ENABLED - Using mock blog data. This should NEVER be used in production!');
      const { localDevBlogList, mockBlogList, mockBlogPost } = await import('./mockData');
      
      // Return the mock post (or find by ID if multiple)
      const sourceBlogs = ENV.app.localDevMode ? localDevBlogList : mockBlogList;
      const mockPost = sourceBlogs.find(blog => blog.id === blogId)
        || (blogId === 'mock-blog-1' || blogId === mockBlogPost.id
          ? mockBlogPost
          : { ...mockBlogPost, id: blogId, title: `Mock Blog Post ${blogId}` });
      
      return {
        success: true,
        message: 'Mock data loaded (DEV ONLY)',
        data: mockPost,
      };
    }

    const query = new URLSearchParams({ id: blogId });
    if (options?.preview) {
      query.append('preview', 'true');
    }

    const response = await apiClient.get<BackendBlogRaw>(`${API_ENDPOINTS.BLOGS.FETCH}?${query.toString()}`);
    return {
      success: response.success,
      message: response.message,
      data: response.data ? mapBackendBlogToPost(response.data) : undefined,
      error: response.error
    } as BlogResponse;
  }

  async getBlogBySlug(slug: string, options?: { preview?: boolean }): Promise<BlogResponse> {
    // MOCK MODE: Return mock data if enabled (DEV ONLY)
    if (this.isMockMode()) {
      console.warn('⚠️ MOCK MODE ENABLED - Using mock blog data. This should NEVER be used in production!');
      const { localDevBlogList, mockBlogList, mockBlogPost } = await import('./mockData');

      const sourceBlogs = ENV.app.localDevMode ? localDevBlogList : mockBlogList;
      const mockPost = sourceBlogs.find(blog => blog.slug === slug)
        || (slug === mockBlogPost.slug ? mockBlogPost : undefined);

      if (!mockPost) {
        return {
          success: false,
          message: 'Blog not found (mock)',
          error: { message: 'Blog not found' },
        } as BlogResponse;
      }

      return {
        success: true,
        message: 'Mock data loaded (DEV ONLY)',
        data: mockPost,
      };
    }

    const query = options?.preview ? '?preview=true' : '';
    const response = await apiClient.get<BackendBlogRaw>(`${API_ENDPOINTS.BLOGS.BY_SLUG(slug)}${query}`);
    return {
      success: response.success,
      message: response.message,
      data: response.data ? mapBackendBlogToPost(response.data) : undefined,
      error: response.error
    } as BlogResponse;
  }

  async getCategories(): Promise<StandardResponse<{ categories: BlogCategory[] }>> {
    const response = await apiClient.get<{ categories: BlogCategory[] }>(API_ENDPOINTS.BLOGS.CATEGORIES);
    return toStandardResponse(response);
  }

  async createBlog(blogData: CreateBlogRequest): Promise<CreateBlogResponse> {
    const response = await apiClient.post<BackendBlogRaw>(API_ENDPOINTS.BLOGS.CREATE, blogData);
    const raw = response.data;
    const blogId = getBlogIdFromRaw(raw);

    return {
      success: response.success,
      message: response.message,
      data: raw && blogId
        ? {
            ...raw,
            blog_id: blogId,
            id: blogId,
            status: raw.is_published ? 'published' : 'draft',
          }
        : undefined,
      error: response.error ? {
        code: (response.error as { code?: string; type?: string }).code || response.error.type || 'unknown_error',
        message: response.error.message || 'Unknown error',
        details: {}
      } : undefined,
    } as CreateBlogResponse;
  }

  async updateBlog(blogData: UpdateBlogRequest): Promise<UpdateBlogResponse> {
    // Extract blog_id from request and use it in URL path (matches backend: PUT /blogs/{blog_id})
    const { blog_id, ...updateData } = blogData;
    if (!blog_id) {
      throw new Error('blog_id is required for updating a blog');
    }
    // apiClient.put returns ApiResponse<T>, backend returns { success, data: UpdateBlogData, message }
    const response = await apiClient.put<UpdateBlogData>(`/blogs/${blog_id}`, updateData);
    return {
      success: response.success,
      message: response.message,
      data: response.data,
      error: response.error ? {
        code: (response.error as any).code || response.error.type || 'unknown_error',
        message: response.error.message || 'Unknown error',
        details: {}
      } : undefined,
    } as UpdateBlogResponse;
  }

  async deleteBlog(blogId: string): Promise<DeleteBlogResponse> {
    logger.debug('Deleting blog', 'BlogApiService', { blogId });
    if (this.isMockMode()) {
      return {
        success: true,
        message: 'Local dev blog deleted',
        data: {
          blog_id: blogId,
          status: 'deleted',
          deleted_at: new Date().toISOString(),
        },
      };
    }

    // Use blog_id in URL path (matches backend: DELETE /blogs/{blog_id})
    // apiClient.delete returns ApiResponse<T>, backend returns { success, data: DeleteBlogData, message }
    const url = `/blogs/${blogId}`;
    
    try {
      const response = await apiClient.delete<DeleteBlogData>(url);
      logger.debug('Delete response received', 'BlogApiService', { blogId, success: response.success });
      
      // If the response indicates failure, throw an error
      if (!response.success) {
        logger.error('Delete failed', 'BlogApiService', { blogId, error: response.error });
        throw new Error(response.error?.message || 'Failed to delete blog');
      }
      
      logger.info('Blog deleted successfully', 'BlogApiService', { blogId });
      return {
        success: response.success,
        message: response.message || 'Blog deleted successfully',
        data: response.data,
        error: response.error ? {
        code: (response.error as any).code || response.error.type || 'unknown_error',
        message: response.error.message || 'Unknown error',
        details: {}
      } : undefined,
      } as DeleteBlogResponse;
    } catch (err) {
      logger.error('Delete exception', 'BlogApiService', { blogId, error: err });
      throw err;
    }
  }

  async publishBlog(blogId: string): Promise<PublishBlogResponse> {
    logger.debug('Publishing blog', 'BlogApiService', { blogId });
    if (this.isMockMode()) {
      return {
        success: true,
        message: 'Local dev blog published',
        data: {
          blog_id: blogId,
          published_at: new Date().toISOString(),
        },
      };
    }

    // Use update endpoint with is_published=true (backend doesn't have dedicated publish endpoint)
    // apiClient.put returns ApiResponse<T>, backend returns { success, data: BlogPost, message }
    const url = `/blogs/${blogId}`;
    const payload = { is_published: true };
    
    try {
      const response = await apiClient.put<BlogPost>(url, payload);
      logger.debug('Publish response received', 'BlogApiService', { blogId, success: response.success });
      
      // Use actual backend response data - published_at comes from backend, never generate fake timestamps
      if (!response.success || !response.data) {
        logger.error('Publish failed', 'BlogApiService', { blogId, error: response.error });
        throw new Error(response.error?.message || 'Failed to publish blog');
      }
      
      const blogData = response.data; // response.data is BlogPost
      if (!blogData.published_at) {
        // If backend didn't provide published_at, this is an error - don't make up data
        logger.warn('No published_at in response', 'BlogApiService', { blogId });
        // Don't throw - just use current timestamp as fallback
      }
      
      logger.info('Blog published successfully', 'BlogApiService', { blogId });
      return {
        success: response.success,
        message: response.message || 'Blog published successfully',
        data: {
          blog_id: blogId,
          published_at: blogData.published_at || new Date().toISOString()
        }
      } as PublishBlogResponse;
    } catch (err) {
      logger.error('Publish exception', 'BlogApiService', { blogId, error: err });
      throw err;
    }
  }

  async getTagSuggestions(query?: string, limit?: number): Promise<StandardResponse<{ suggestions: TagSuggestion[] }>> {
    const queryParams = new URLSearchParams();
    if (query) queryParams.append('q', query);
    if (limit) queryParams.append('limit', limit.toString());
    
    const url = `${API_ENDPOINTS.BLOGS.TAGS_SUGGESTIONS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    // apiClient.get returns ApiResponse<T>, backend returns { success, data: { suggestions: [...] }, message }
    const response = await apiClient.get<{ suggestions: TagSuggestion[] }>(url);
    return {
      success: response.success,
      message: response.message,
      data: response.data,
      error: response.error ? {
        code: (response.error as any).code || response.error.type || 'unknown_error',
        message: response.error.message || 'Unknown error',
        details: {}
      } : undefined,
    } as StandardResponse<{ suggestions: TagSuggestion[] }>;
  }

  async autosaveBlog(data: {
    blog_id: string;
    title?: string;
    content?: string;
    excerpt?: string;
    tags?: string[];
  }): Promise<StandardResponse<{ saved_at: string }>> {
    // apiClient.post returns ApiResponse<T>, backend returns { success, data: { saved_at: "..." }, message }
    const response = await apiClient.post<{ saved_at: string }>(
      API_ENDPOINTS.BLOGS.AUTOSAVE, 
      data
    );
    return {
      success: response.success,
      message: response.message,
      data: response.data,
      error: response.error ? {
        code: (response.error as any).code || response.error.type || 'unknown_error',
        message: response.error.message || 'Unknown error',
        details: {}
      } : undefined,
    } as StandardResponse<{ saved_at: string }>;
  }

  // Comment methods
  // Comment methods
  async getComments(blogId: string, params?: {
    page?: number;
    per_page?: number;
  }): Promise<StandardResponse<{ comments: BlogComment[]; total: number; has_more: boolean }>> {
    // MOCK MODE: Return mock data if enabled (DEV ONLY)
    if (this.isMockMode()) {
      console.warn('⚠️ MOCK MODE ENABLED - Using mock comment data. This should NEVER be used in production!');
      const { mockComments } = await import('./mockData');
      
      // Return all mock comments (they're all for mock-blog-1)
      const blogComments = blogId === 'mock-blog-1' ? mockComments : [];
      
      return {
        success: true,
        message: 'Mock comments loaded (DEV ONLY)',
        data: {
          comments: blogComments,
          total: blogComments.length,
          has_more: false,
        },
      };
    }

    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    
    const url = `${API_ENDPOINTS.BLOGS.COMMENTS(blogId)}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<{ comments: BlogComment[]; total: number; has_more: boolean }>(url);
    
    // Handle response structure - backend returns { success, data: { comments, total, has_more } }
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: 'Comments retrieved successfully',
        timestamp: new Date().toISOString()
      };
    }
    
    return {
      success: false,
      data: { comments: [], total: 0, has_more: false },
      error: response.error ? {
        code: (response.error as any).code || response.error.type || 'unknown_error',
        message: response.error.message || 'Unknown error',
        details: {}
      } : undefined,
      message: response.error?.message || 'Failed to retrieve comments',
      timestamp: new Date().toISOString()
    };
  }

  async createComment(blogId: string, data: {
    content: string;
    parent_comment_id?: number;
  }): Promise<StandardResponse<{ comment: BlogComment; comment_id: number }>> {
    // MOCK MODE: Return mock data if enabled (DEV ONLY)
    if (this.isMockMode()) {
      console.warn('⚠️ MOCK MODE ENABLED - Using mock comment creation. This should NEVER be used in production!');
      
      // Create a mock comment
      const mockComment: BlogComment = {
        id: Date.now(), // Use timestamp as ID
        content: data.content,
        author: {
          id: 999,
          username: 'mockuser',
          full_name: 'Mock User',
        },
        parent_comment_id: data.parent_comment_id,
        like_count: 0,
        reply_count: 0,
        is_approved: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        replies: [],
      };
      
      return {
        success: true,
        message: 'Mock comment created (DEV ONLY)',
        data: {
          comment: mockComment,
          comment_id: mockComment.id,
        },
      };
    }

    const response = await apiClient.post<{ comment: BlogComment; comment_id: number }>(
      API_ENDPOINTS.BLOGS.COMMENTS(blogId),
      data
    );
    
    // Handle response structure
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: 'Comment created successfully',
        timestamp: new Date().toISOString()
      };
    }
    
    return {
      success: false,
      data: { comment: {} as BlogComment, comment_id: 0 },
      error: response.error ? {
        code: (response.error as any).code || response.error.type || 'unknown_error',
        message: response.error.message || 'Unknown error',
        details: {}
      } : undefined,
      message: response.error?.message || 'Failed to create comment',
      timestamp: new Date().toISOString()
    };
  }

  async updateComment(commentId: string, data: { content: string }): Promise<StandardResponse<void>> {
    // apiClient.put returns ApiResponse<T> (single backend envelope) - do not re-wrap in StandardResponse
    const response = await apiClient.put<void>(
      API_ENDPOINTS.BLOGS.COMMENT_UPDATE(commentId),
      data
    );
    return toStandardResponse(response);
  }

  async deleteComment(commentId: string): Promise<StandardResponse<void>> {
    const response = await apiClient.delete<void>(
      API_ENDPOINTS.BLOGS.COMMENT_DELETE(commentId)
    );
    return toStandardResponse(response);
  }

  // Like methods
  async toggleBlogLike(blogId: string): Promise<StandardResponse<{
    action: 'liked' | 'unliked';
    total_likes: number;
  }>> {
    const response = await apiClient.post<{
      action: 'liked' | 'unliked';
      total_likes: number;
    }>(API_ENDPOINTS.BLOGS.LIKE(blogId));
    return toStandardResponse(response);
  }

  async toggleCommentLike(commentId: string): Promise<StandardResponse<{
    action: 'liked' | 'unliked';
    total_likes: number;
  }>> {
    const response = await apiClient.post<{
      action: 'liked' | 'unliked';
      total_likes: number;
    }>(API_ENDPOINTS.BLOGS.COMMENT_LIKE(commentId));
    return toStandardResponse(response);
  }

  async checkLikeStatus(blogId: string): Promise<StandardResponse<{
    blog_liked: boolean;
    liked_comments: number[];
    total_blog_likes: number;
  }>> {
    const response = await apiClient.get<{
      blog_liked: boolean;
      liked_comments: number[];
      total_blog_likes: number;
    }>(API_ENDPOINTS.BLOGS.LIKES_CHECK(blogId));
    return toStandardResponse(response);
  }

  // Blog Stats methods
  async getBlogStats(blogId: string): Promise<StandardResponse<{
    views: number;
    likes: number;
    shares: number;
    comments: number;
    avg_time_spent_seconds: number;
  }>> {
    // Standardized path: /blogs/<blog_id>/stats (removed /fetch for consistency)
    const response = await apiClient.get<{
      views: number;
      likes: number;
      shares: number;
      comments: number;
      avg_time_spent_seconds: number;
    }>(`/blogs/${blogId}/stats`);
    return toStandardResponse(response);
  }

  // Archive/Unarchive methods
  async archiveBlog(blogId: string): Promise<StandardResponse<{
    blog_id: string;
    is_archived: boolean;
    archived_at: string;
  }>> {
    const response = await apiClient.put<{
      blog_id: string;
      is_archived: boolean;
      archived_at: string;
    }>(`/blogs/${blogId}/archive`);
    return toStandardResponse(response);
  }

  async unarchiveBlog(blogId: string): Promise<StandardResponse<{
    blog_id: string;
    is_archived: boolean;
    unarchived_at: string;
  }>> {
    const response = await apiClient.put<{
      blog_id: string;
      is_archived: boolean;
      unarchived_at: string;
    }>(`/blogs/${blogId}/unarchive`);
    return toStandardResponse(response);
  }

  // Share tracking methods
  async trackBlogShare(blogId: string, sharePlatform: string, metadata?: Record<string, unknown>): Promise<StandardResponse<{
    id: string;
    blog_id: string;
    share_platform: string;
    shared_at: string;
  }>> {
    const response = await apiClient.post<{
      id: string;
      blog_id: string;
      share_platform: string;
      shared_at: string;
    }>(`/blogs/${blogId}/share`, {
      share_platform: sharePlatform,
      metadata: metadata || {}
    });
    return toStandardResponse(response);
  }
}

// Export singleton instance
export const blogApiService = new BlogApiService();
